import { cn } from '@liquidbudget/common';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useScrollStore } from '@/components/ui/use-scroll-store';

// Define a type for the enhanced ScrollArea that includes scrollKey
interface PositionPreservingScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  scrollKey?: string;
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  PositionPreservingScrollAreaProps
>(({ className, children, scrollKey, ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { savePosition, getPosition } = useScrollStore();

  // Handle scroll position preservation if scrollKey is provided
  React.useEffect(() => {
    if (!scrollKey) return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    // Save scroll position on scroll
    const handleScroll = () => {
      savePosition(scrollKey, viewport.scrollTop);
    };

    viewport.addEventListener('scroll', handleScroll);
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [scrollKey, savePosition]);

  // Restore scroll position after navigation
  React.useEffect(() => {
    if (!scrollKey) return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    // Use a small timeout to ensure DOM is fully updated
    const timer = setTimeout(() => {
      viewport.scrollTop = getPosition(scrollKey);
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname, scrollKey, getPosition]);

  return (
    <ScrollAreaPrimitive.Root ref={ref} className={cn('relative overflow-hidden', className)} {...props}>
      <ScrollAreaPrimitive.Viewport ref={viewportRef} className="h-full w-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner className="bg-gray-100 dark:bg-gray-800" />
    </ScrollAreaPrimitive.Root>
  );
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      // Add background for the track area
      'bg-gray-100 dark:bg-gray-800',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb
      className={cn(
        'relative flex-1 rounded-full',
        // Change thumb color from bg-border to more prominent colors
        'bg-gray-400 dark:bg-gray-500 hover:bg-gray-500 dark:hover:bg-gray-400',
      )}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
